import React from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Search as SearchIcon, Settings2, CircleHelp, ChevronDown, ArrowLeft, UserCircle2, LayoutGrid, LogOut, ChevronRight } from "lucide-react";
import MeldingenCentrum from "@/components/MeldingenCentrum";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { itemVoorPad, DOMEIN_MAP } from "@/lib/menuDomeinen";
import { LayoutV2MenuItems, LayoutV2TestersHost } from "@/components/layout_v2/LayoutV2Schakelaar";
import { rolLabel } from "@/lib/rollen";
import { LayoutV3MenuItem } from "@/components/v3/V3TopBar";


/** Bovenbalk (desktop): broodkruimel hub › pagina, centrale ⌘K-zoekknop, meldingen, instellingen, help en account. */
export function TopBar({ hubs, user, onMenuInstellingen, onLogout, onHub }) {
  const location = useLocation();
  const nav = useNavigate();
  const item = itemVoorPad(hubs, location.pathname);
  const startMatch = location.pathname.match(/^\/start\/([a-z]+)/);
  const hub = item?.hub || (startMatch ? hubs.find((h) => h.key === (DOMEIN_MAP[startMatch[1]] ? startMatch[1] : null)) : null) || (location.pathname === "/alle-tools" ? null : null);
  const paginaLabel = item?.label || (location.pathname === "/alle-tools" ? "Alle tools" : startMatch && hub ? `Startpagina ${hub.label}` : "");
  const thuis = location.pathname === "/dashboard";
  return (
    <header role="banner" data-testid="topbar" className="hidden lg:flex sticky top-0 z-30 h-14 items-center gap-4 px-5 bg-white/95 backdrop-blur-md border-b border-slate-200">
      <div className="flex items-center gap-2 min-w-0 flex-1" data-testid="topbar-breadcrumb">
        {!thuis && (
          <button type="button" onClick={() => (window.history.length > 2 ? nav(-1) : nav("/dashboard"))} className="p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors" aria-label="Terug" title="Terug" data-testid="desktop-back-btn">
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}
        {hub && (
          <button type="button" onClick={() => onHub(hub.key)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors" data-testid="topbar-hub-crumb">
            <span className={`h-2 w-2 rounded-full ${hub.kleur}`} aria-hidden="true" />{hub.label}
          </button>
        )}
        {hub && paginaLabel && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}
        {paginaLabel && <span className="text-sm font-semibold text-slate-900 truncate" data-testid="topbar-page-title">{paginaLabel}</span>}
      </div>
      <button type="button" onClick={() => window.dispatchEvent(new Event("bpt:open-cmdk"))} data-testid="topbar-search-trigger" title="Zoek in alle data (⌘K)"
        className="hidden xl:flex items-center gap-2 w-[360px] h-9 px-3 rounded-lg bg-slate-100 border border-slate-300 text-sm text-slate-500 hover:border-slate-400 hover:bg-white transition-colors">
        <SearchIcon className="h-4 w-4" />
        <span className="flex-1 text-left">Zoek klanten, werkbonnen, facturen…</span>
        <kbd className="px-1.5 py-0.5 rounded bg-white border border-slate-300 text-[10px] font-mono text-slate-500">⌘K</kbd>
      </button>
      <div className="flex items-center gap-1 flex-1 justify-end">
        <button type="button" onClick={() => window.dispatchEvent(new Event("bpt:open-cmdk"))} className="xl:hidden p-2 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors" aria-label="Zoeken" data-testid="topbar-search-icon">
          <SearchIcon className="h-4 w-4" />
        </button>
        <div data-testid="topbar-notifications-btn"><MeldingenCentrum variant="desktop" /></div>
        <button type="button" onClick={onMenuInstellingen} className="p-2 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors" aria-label="Menu aanpassen" title="Menu aanpassen" data-testid="topbar-settings-btn" data-rechten="lezen">
          <Settings2 className="h-4 w-4" />
        </button>
        <Link to="/handboek" className="p-2 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors" aria-label="Help / handboek" title="Handboek" data-testid="topbar-help-btn">
          <CircleHelp className="h-4 w-4" />
        </Link>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="ml-1 flex items-center gap-2 pl-1 pr-2 py-1 rounded-full border border-slate-200 hover:border-slate-400 hover:bg-slate-50 transition-colors" data-testid="topbar-account-menu" aria-label="Account">
              <span className="relative">
                <span className="h-7 w-7 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold">{(user?.name || "?").slice(0, 1).toUpperCase()}</span>
                <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white" aria-hidden="true" />
              </span>
              <span className="hidden 2xl:block text-left leading-tight">
                <span className="block text-xs font-semibold text-slate-900 max-w-[140px] truncate">{user?.name}</span>
                <span className="block text-[10px] uppercase tracking-wider text-slate-500" data-testid="topbar-role-label">{rolLabel(user?.role)}</span>
              </span>
              <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60" data-testid="topbar-account-dropdown">
            <DropdownMenuLabel className="font-normal">
              <div className="text-sm font-semibold text-slate-900 truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">{rolLabel(user?.role)}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => nav("/mijn-gegevens")} data-testid="account-mijn-gegevens"><UserCircle2 className="h-4 w-4 mr-2" /> Mijn gegevens</DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav("/alle-tools")} data-testid="account-alle-tools"><LayoutGrid className="h-4 w-4 mr-2" /> Alle tools</DropdownMenuItem>
            <DropdownMenuItem onClick={onMenuInstellingen} data-testid="account-menu-aanpassen"><Settings2 className="h-4 w-4 mr-2" /> Menu aanpassen</DropdownMenuItem>
            <LayoutV3MenuItem />
            <LayoutV2MenuItems />
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} className="text-red-700 focus:text-red-800" data-testid="account-logout"><LogOut className="h-4 w-4 mr-2" /> Uitloggen</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <LayoutV2TestersHost />
      </div>
    </header>
  );
}
